/**
 * ColumnPicker — which columns a screen shows, and in what order.
 *
 * Exists because AG Grid's column tool panel is **Enterprise only**: on
 * Community there is no built-in UI for column visibility at all.
 *
 * A dialog with two tabs (mock: `docs/column-picker-mock.html`, owner
 * 2026-10-26), replacing a 300 px list of ~120 checkboxes grouped only by
 * timeframe:
 *
 * - **Presets** — the `COLUMN_SETS` as cards; one click applies one, adding to
 *   what is shown or replacing it.
 * - **Browse** — topics down the left (the server's categories, the same word
 *   the header band shows), one row per measure with a chip per timeframe, and
 *   search over name, meaning and field.
 *
 * Beside both, **On screen**: the draft in grid order, dragged to reorder.
 * **Nothing is written until Apply** — the dialog edits a list of field names
 * and `applyDraft` turns it into the contract's column view once, so Cancel is
 * a real undo and a save happens once rather than per tick.
 *
 * Opened from the screener bar's ▥ and from the screen editor's **+ Add
 * columns**, so there is one way to choose columns.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { ScreenerColumn } from "@qufolio/model";

import { COLUMN_SETS } from "../lib/column-sets.js";
import {
  applyDraft,
  applyPreset,
  moveInDraft,
  pickerRows,
  presetSize,
  searchRows,
  shownFields,
  toggleDraft,
  topicsOf,
  variantLabel,
  withSuggested,
  type PickerRow,
} from "../lib/column-picker.js";
import { timeframeBadge, type UniverseColumn } from "../lib/column-universe.js";

export interface ColumnPickerProps {
  universe: readonly UniverseColumn[];
  columns: readonly ScreenerColumn[];
  onChange: (columns: ScreenerColumn[]) => void;
  onClose: () => void;
  /** Which tab opens first. Presets, unless the caller is adding to a screen. */
  initialTab?: "presets" | "browse";
}

/** Below this the topics become a dropdown and the On screen pane folds away. */
const NARROW_PX = 640;

export function ColumnPicker({
  universe,
  columns,
  onChange,
  onClose,
  initialTab = "presets",
}: ColumnPickerProps) {
  const [tab, setTab] = useState<"presets" | "browse">(initialTab);
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState<string[]>(() => shownFields(universe));
  const [mode, setMode] = useState<"replace" | "add">("replace");
  const [picked, setPicked] = useState<string | null>(null);
  const [dragField, setDragField] = useState<string | null>(null);

  // The suggested computed columns (ATH, % from ATH) list beside the server's.
  const offered = useMemo(() => withSuggested(universe), [universe]);
  const rows = useMemo(() => pickerRows(offered), [offered]);
  const topics = useMemo(() => topicsOf(rows), [rows]);
  const [topic, setTopic] = useState<string>(() => topics[0]?.topic ?? "");
  const byField = useMemo(() => new Map(offered.map((u) => [u.field, u])), [offered]);
  const shown = useMemo(() => new Set(draft), [draft]);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const el = dialogRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => setNarrow((entry?.contentRect.width ?? 999) < NARROW_PX));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const searching = query.trim() !== "";
  const listed: PickerRow[] = searching
    ? searchRows(rows, query)
    : (topics.find((t) => t.topic === topic)?.rows ?? []);

  const rowOn = (row: PickerRow) => row.variants.some((v) => shown.has(v.field));
  const toggleRow = (row: PickerRow, on: boolean) => {
    if (on) setDraft((d) => toggleDraft(d, row.variants[0]!.field, true));
    else setDraft((d) => d.filter((f) => !row.variants.some((v) => v.field === f)));
  };
  const setAll = (on: boolean) => {
    for (const row of listed) toggleRow(row, on);
  };

  const apply = () => {
    const next = applyDraft(columns, draft);
    onChange(next);
    onClose();
  };

  const onScreenCount = draft.length;

  const presets = (
    <div style={{ ...scroll, flex: 1, padding: "0.75rem" }}>
      <div style={presetGrid}>
        {COLUMN_SETS.map((set) => {
          const size = presetSize(universe, set);
          const names = set.fields.filter((f) => byField.has(f)).map((f) => byField.get(f)!.header);
          return (
            <button
              key={set.id}
              type="button"
              disabled={size === 0}
              onClick={() => {
                setDraft((d) => applyPreset(d, universe, set, mode));
                setPicked(set.id);
              }}
              style={{
                ...presetCard,
                borderColor: picked === set.id ? "var(--qf-accent)" : "var(--qf-border)",
                background: picked === set.id ? "var(--qf-selection)" : "var(--qf-background)",
                opacity: size === 0 ? 0.5 : 1,
                cursor: size === 0 ? "default" : "pointer",
              }}
            >
              <span style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <b style={{ flex: 1 }}>{set.label}</b>
                <span style={muted}>{size === 0 ? "not on this screen" : `${size} cols`}</span>
              </span>
              <span style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 6 }}>
                {names.map((name, i) => (
                  <span key={`${name}${i}`} style={chip}>
                    {name}
                  </span>
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const topicNav = narrow ? (
    <select
      aria-label="Topic"
      value={searching ? "" : topic}
      onChange={(e) => {
        setQuery("");
        setTopic(e.target.value);
      }}
      style={{ ...inputStyle, margin: "0.5rem 0.75rem 0" }}
    >
      {topics.map((t) => (
        <option key={t.topic} value={t.topic}>
          {t.topic} ({t.rows.filter(rowOn).length}/{t.rows.length})
        </option>
      ))}
    </select>
  ) : (
    <nav style={{ ...scroll, width: 180, flex: "0 0 auto", borderRight: "1px solid var(--qf-border)", padding: "0.4rem" }}>
      {topics.map((t) => {
        const on = t.rows.filter(rowOn).length;
        const active = !searching && t.topic === topic;
        return (
          <button
            key={t.topic}
            type="button"
            onClick={() => {
              setQuery("");
              setTopic(t.topic);
            }}
            style={{
              ...navButton,
              background: active ? "var(--qf-selection)" : "transparent",
              color: active ? "var(--qf-accent)" : "var(--qf-text)",
            }}
          >
            <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{t.topic}</span>
            <span style={muted}>
              {on > 0 && <b style={{ color: "var(--qf-text)" }}>{on}</b>}
              {on > 0 && "/"}
              {t.rows.length}
            </span>
          </button>
        );
      })}
    </nav>
  );

  const browse = (
    <div style={{ display: "flex", flexDirection: narrow ? "column" : "row", flex: 1, minHeight: 0 }}>
      {topicNav}
      <div style={{ ...scroll, flex: 1, padding: "0.5rem 0.75rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.4rem" }}>
          <b style={{ flex: 1 }}>{searching ? `${listed.length} matching “${query.trim()}”` : topic}</b>
          <button type="button" onClick={() => setAll(true)} style={linkButton}>
            Add all
          </button>
          <button type="button" onClick={() => setAll(false)} style={linkButton}>
            Remove all
          </button>
        </div>
        {listed.length === 0 && <div style={{ ...muted, padding: "0.75rem 0" }}>No columns match.</div>}
        {listed.map((row) => {
          const on = rowOn(row);
          const fields = row.variants.map((v) => v.field).join(" · ");
          return (
            <label
              key={row.key}
              style={{
                ...pickRow,
                background: on ? "var(--qf-surface)" : "transparent",
                borderColor: on ? "var(--qf-border)" : "transparent",
              }}
            >
              <input type="checkbox" checked={on} onChange={(e) => toggleRow(row, e.target.checked)} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 600 }}>{row.header}</span>
                {fields !== row.header && <code style={fieldCode}>{row.variants[0]!.field}</code>}
                {row.description && <span style={{ ...muted, display: "block" }}>{row.description}</span>}
              </span>
              {(row.variants.length > 1 || row.variants[0]!.timeframe) && (
                <span style={{ display: "flex", gap: 3 }} onClick={(e) => e.preventDefault()}>
                  {row.variants.map((v) => {
                    const vOn = shown.has(v.field);
                    return (
                      <button
                        key={v.field}
                        type="button"
                        aria-pressed={vOn}
                        title={`${v.header}${v.timeframe ? ` — ${timeframeBadge(v.timeframe)}` : ""}`}
                        onClick={() => setDraft((d) => toggleDraft(d, v.field, !vOn))}
                        style={{
                          ...tfChip,
                          background: vOn ? "var(--qf-accent)" : "var(--qf-selection)",
                          color: vOn ? "var(--qf-background)" : "var(--qf-text-secondary)",
                        }}
                      >
                        {variantLabel(v)}
                      </button>
                    );
                  })}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );

  const onScreen = !narrow && (
    <aside style={{ width: 230, flex: "0 0 auto", borderLeft: "1px solid var(--qf-border)", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", padding: "0.6rem 0.6rem 0.3rem" }}>
        <b style={{ flex: 1 }}>On screen ({onScreenCount})</b>
        <button type="button" onClick={() => setDraft([])} style={linkButton}>
          Clear
        </button>
      </div>
      <div style={{ ...scroll, flex: 1, padding: "0 0.4rem 0.5rem" }}>
        {draft.map((field) => {
          const u = byField.get(field);
          return (
            <div
              key={field}
              draggable
              onDragStart={(e) => {
                setDragField(field);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", field);
              }}
              onDragEnd={() => setDragField(null)}
              onDragOver={(e) => {
                if (dragField === null) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
              }}
              onDrop={(e) => {
                if (dragField === null) return;
                e.preventDefault();
                setDraft((d) => moveInDraft(d, dragField, field));
                setDragField(null);
              }}
              style={{ ...onScreenRow, opacity: dragField === field ? 0.4 : 1 }}
            >
              <span style={{ color: "var(--qf-text-muted)", cursor: "grab" }}>⠿</span>
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {u?.header || field}
              </span>
              {u?.timeframe && <span style={chip}>{timeframeBadge(u.timeframe)}</span>}
              <button
                type="button"
                aria-label={`Remove ${u?.header || field}`}
                onClick={() => setDraft((d) => toggleDraft(d, field, false))}
                style={iconButton}
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>
    </aside>
  );

  return (
    <div style={backdrop} onClick={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-label="Columns"
        onClick={(e) => e.stopPropagation()}
        style={dialog}
      >
        <header style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.6rem 0.75rem", borderBottom: "1px solid var(--qf-border)", flexWrap: "wrap" }}>
          <b>Columns</b>
          <span style={tabs}>
            {(["presets", "browse"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                style={{
                  ...tabButton,
                  background: tab === t ? "var(--qf-surface-alt)" : "transparent",
                  color: tab === t ? "var(--qf-text)" : "var(--qf-text-muted)",
                }}
              >
                {t === "presets" ? "Presets" : "Browse"}
              </button>
            ))}
          </span>
          <input
            autoFocus
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (e.target.value.trim()) setTab("browse");
            }}
            placeholder="Search by name or meaning…"
            style={{ ...inputStyle, flex: "1 1 160px", minWidth: 0 }}
          />
          <button type="button" onClick={onClose} title="Close" style={iconButton}>
            ✕
          </button>
        </header>

        <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
          {tab === "presets" ? presets : browse}
          {onScreen}
        </div>

        <footer style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.6rem 0.75rem", borderTop: "1px solid var(--qf-border)", flexWrap: "wrap" }}>
          <span style={muted}>
            {onScreenCount} on screen · {universe.length} available
          </span>
          <span style={{ flex: 1 }} />
          {tab === "presets" && (
            <span style={{ ...muted, display: "flex", gap: "0.6rem", alignItems: "center" }}>
              A preset will
              <label style={radio}>
                <input type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} /> replace my columns
              </label>
              <label style={radio}>
                <input type="radio" checked={mode === "add"} onChange={() => setMode("add")} /> add to them
              </label>
            </span>
          )}
          <button type="button" onClick={onClose} style={button}>
            Cancel
          </button>
          <button type="button" onClick={apply} style={{ ...button, ...primary }}>
            Apply
          </button>
        </footer>
      </div>
    </div>
  );
}

/** The timeframe chip, shared with the grid headers. */
export function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        flex: "0 0 auto",
        padding: "0 0.25rem",
        borderRadius: 3,
        background: "var(--qf-selection)",
        color: "var(--qf-text-secondary)",
        fontSize: "0.62rem",
        fontWeight: 600,
        letterSpacing: "0.04em",
      }}
    >
      {children}
    </span>
  );
}

// Fixed, not absolute: the picker opens from the screener bar and from inside
// the screen editor, and only the viewport is the right size in both.
const backdrop: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1000,
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const dialog: React.CSSProperties = {
  width: "min(960px, calc(100vw - 16px))",
  height: "min(680px, calc(100vh - 32px))",
  display: "flex",
  flexDirection: "column",
  background: "var(--qf-surface-alt)",
  border: "1px solid var(--qf-border)",
  borderRadius: 8,
  boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
  color: "var(--qf-text)",
  fontSize: "0.8rem",
};

const scroll: React.CSSProperties = { overflowY: "auto", minHeight: 0 };
const muted: React.CSSProperties = { color: "var(--qf-text-muted)", fontSize: "0.72rem" };

const inputStyle: React.CSSProperties = {
  padding: "0.3rem 0.45rem",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  color: "var(--qf-text)",
  fontSize: "0.8rem",
};

const tabs: React.CSSProperties = {
  display: "inline-flex",
  gap: 2,
  padding: 2,
  borderRadius: 5,
  background: "var(--qf-background)",
};

const tabButton: React.CSSProperties = {
  border: "none",
  borderRadius: 4,
  padding: "0.25rem 0.75rem",
  cursor: "pointer",
  fontSize: "0.8rem",
};

const presetGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  gap: "0.6rem",
  alignContent: "start",
};

const presetCard: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  textAlign: "left",
  padding: "0.6rem 0.7rem",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  color: "var(--qf-text)",
  fontSize: "0.8rem",
};

const chip: React.CSSProperties = {
  padding: "0 0.35rem",
  borderRadius: 3,
  background: "var(--qf-selection)",
  color: "var(--qf-text-secondary)",
  fontSize: "0.68rem",
};

const navButton: React.CSSProperties = {
  display: "flex",
  width: "100%",
  gap: 6,
  alignItems: "center",
  border: "none",
  borderRadius: 4,
  padding: "0.35rem 0.5rem",
  cursor: "pointer",
  textAlign: "left",
  fontSize: "0.8rem",
};

const pickRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.55rem",
  padding: "0.35rem 0.5rem",
  border: "1px solid transparent",
  borderRadius: 5,
  cursor: "pointer",
};

const fieldCode: React.CSSProperties = {
  marginLeft: 6,
  color: "var(--qf-text-muted)",
  fontSize: "0.68rem",
};

const tfChip: React.CSSProperties = {
  minWidth: 26,
  border: "none",
  borderRadius: 3,
  padding: "0.1rem 0.3rem",
  fontSize: "0.68rem",
  fontWeight: 600,
  cursor: "pointer",
};

const onScreenRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "0.25rem 0.4rem",
  marginBottom: 3,
  borderRadius: 4,
  background: "var(--qf-background)",
};

const radio: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 3, cursor: "pointer" };

const button: React.CSSProperties = {
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "0.3rem 0.9rem",
  background: "var(--qf-background)",
  color: "var(--qf-text)",
  cursor: "pointer",
  fontSize: "0.8rem",
};

const primary: React.CSSProperties = {
  background: "var(--qf-accent)",
  borderColor: "var(--qf-accent)",
  color: "var(--qf-background)",
};

const iconButton: React.CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--qf-text-muted)",
  cursor: "pointer",
  fontSize: "0.8rem",
  padding: "0.15rem 0.3rem",
};

const linkButton: React.CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--qf-accent)",
  cursor: "pointer",
  fontSize: "0.72rem",
  padding: 0,
};
